import React, { useContext, useEffect, useMemo, useState } from 'react';
import { curveNatural } from 'd3-shape';
import '@d3fc/d3fc-element';
import { extentLinear } from '@d3fc/d3fc-extent';
import { seriesSvgMulti } from '@d3fc/d3fc-series';
import AppContext from 'app/AppContext';
import TimeSeriesChart from 'utils/components/TimeSeriesChart/TimeSeriesChart';
import gridlines from 'utils/components/TimeSeriesChart/gridlines';
import { line } from 'utils/components/TimeSeriesChart/line';
import {
    useBpsFormat,
    useYScale,
} from 'utils/components/TimeSeriesChart/yAxis';
import {
    deriveRelativePerfIndex,
    deriveRelativePerfOrderToIndex,
    FillsGraphData,
} from 'widgets/fillsGraph/fillsGraphData';
import PerformanceVsIndexChartLegend from './PerformanceVsIndexChartLegend';
import './PerformanceVsIndexChart.scss';
import {
    FillsChartType,
    FillsGraphConfiguration,
} from '../store/configuration/FillsGraphConfiguration';
import {
    posNegGradientLine,
    splitPosNeg,
} from 'utils/components/TimeSeriesChart/posNegGradientLine';
import { combineToggleableClassnames } from 'utils/toggleableClassnames/toggleableClassnames';
import { useShowCollapsableCompanionChart } from 'widgets/fillsGraph/collapsableCompanionCharts/useShowCollapsableCompanionChart';
import { withOppositeAccessor } from 'utils/d3fc/accessors';
import { FillsGraphPortfolioData } from 'widgets/fillsGraph/fillsGraphPortfolioData';

const performanceVsIndexChartSeries = [
    line(deriveRelativePerfIndex, 'index-line'),
    ...posNegGradientLine('relativePerfOrderToIndex'),
].map((lineSeries) => lineSeries.curve(curveNatural));

export interface PerformanceVsIndexChartProps {
    height: number;
    chartHeight: number;
    fillsGraphData: FillsGraphData;
    fillsGraphPortfolioData: FillsGraphPortfolioData;
    fillsGraphContainerLeft: number;
    config: FillsGraphConfiguration;
    displayXAxis: boolean;
}

export default function PerformanceVsIndexChart(
    props: PerformanceVsIndexChartProps
) {
    const {
        chartHeight,
        height,
        fillsGraphData,
        fillsGraphPortfolioData,
        config,
        displayXAxis,
        fillsGraphContainerLeft,
    } = props;
    const { data, secondarySymbol } = fillsGraphData;
    const {
        general: { useSecondarySymbol, fallbackIndex },
        [FillsChartType.PerformanceVsIndex]: {
            displayPerformanceVsIndex,
            displayIndex,
        },
    } = config;
    const { numberFormat } = useContext(AppContext);

    const renderCompanionChart = useShowCollapsableCompanionChart(
        FillsChartType.PerformanceVsIndex
    );

    const yDomain =
        data.length > 0 &&
        extentLinear()
            .accessors([
                ...withOppositeAccessor(deriveRelativePerfIndex),
                ...withOppositeAccessor(deriveRelativePerfOrderToIndex),
            ])
            .pad([0.1, 0.1])(data);
    const yScale = useYScale(yDomain, [-1, +1]);
    const yTicks = 3;

    const dataToBind = splitPosNeg(
        data,
        'relativePerfOrderToIndex',
        'relativePerfIndex'
    );

    const [series, setSeries] = useState();
    useEffect(() => {
        setSeries(() =>
            seriesSvgMulti().series([
                gridlines(yTicks, true),
                ...performanceVsIndexChartSeries,
            ])
        );
    }, [yTicks]);

    const bpsFormat = useBpsFormat(numberFormat);

    const relativePerformanceIndex =
        secondarySymbol && useSecondarySymbol ? secondarySymbol : fallbackIndex;

    const cssClasses = combineToggleableClassnames(
        { classname: 'PerformanceVsIndexChart' },
        { classname: 'companion-chart' },
        { classname: 'hide-x-axis', hideClassname: displayXAxis },
        {
            classname: 'hide-performance-vs-index',
            hideClassname: displayPerformanceVsIndex,
        },
        {
            classname: 'hide-index',
            hideClassname: displayIndex,
        }
    );
    const style = useMemo(() => ({ height }), [height]);

    return (
        <div className={cssClasses} style={style}>
            <PerformanceVsIndexChartLegend
                fillsGraphData={fillsGraphData}
                fillsGraphPortfolioData={fillsGraphPortfolioData}
                relativePerformanceIndex={relativePerformanceIndex || ''}
                fillsGraphContainerLeft={fillsGraphContainerLeft}
            />
            {renderCompanionChart && (
                <div className="chart-container">
                    <TimeSeriesChart
                        id="PerformanceVsIndexChart"
                        height={chartHeight}
                        data={dataToBind}
                        series={series}
                        displayXAxis={displayXAxis}
                        yScale={yScale}
                        yTicks={yTicks}
                        yTickFormat={bpsFormat}
                    />
                </div>
            )}
        </div>
    );
}
